/**
 * Schemas for the `subscribe.*` tRPC procedures.
 *
 * Public mailing-list double-opt-in flow: `subscribe.create` records the
 * intent and emails a confirmation token; `subscribe.confirm` flips
 * `confirmed_at` and clears the (single-use) token; `subscribe.unsubscribe`
 * sets `unsubscribed_at` AND adds the address to the suppression list (so
 * a future re-signup attempt does not silently re-mail them); and
 * `subscribe.preferences` lets a confirmed subscriber adjust digest
 * cadence / locale / push-enabled, authenticated by their permanent
 * `unsub_token`.
 *
 * The frequency wire-format uses kebab-case (`urgent-only`) because the
 * web hyphen is the convention everywhere else in the public API; the DB
 * column is the snake_case enum (`urgent_only`). Translation happens at
 * the router boundary.
 */

import { z } from "zod";

import { PUBLIC_LOCALES, publicLocaleSchema } from "./locales.js";

export const SUBSCRIBER_LOCALES = PUBLIC_LOCALES;
export const subscriberLocaleSchema = publicLocaleSchema;
export type SubscriberLocale = z.infer<typeof subscriberLocaleSchema>;

export const SUBSCRIBER_DIGEST_FREQUENCIES = [
  "daily",
  "weekly",
  "urgent-only",
] as const;
export const subscriberDigestFrequencySchema = z.enum(
  SUBSCRIBER_DIGEST_FREQUENCIES,
);
export type SubscriberDigestFrequency = z.infer<
  typeof subscriberDigestFrequencySchema
>;

/** Email length cap matches RFC 5321 (320 chars). */
export const subscriberEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(320);

export const subscribeCreateInputSchema = z.object({
  email: subscriberEmailSchema,
  locale: subscriberLocaleSchema.default("en"),
  digestFrequency: subscriberDigestFrequencySchema.default("daily"),
});

/**
 * Client-side form schema. Mirrors `subscribeCreateInputSchema` but
 * keeps zero defaults so react-hook-form drives initial values from the
 * UI locale and surfaces "required" errors correctly when fields are
 * cleared.
 */
export const subscribeFormSchema = z.object({
  email: subscriberEmailSchema,
  locale: subscriberLocaleSchema,
  digestFrequency: subscriberDigestFrequencySchema,
});
export type SubscribeFormValues = z.infer<typeof subscribeFormSchema>;

export const subscribeCreateOutputSchema = z.object({
  status: z.enum(["sent", "already-confirmed"]),
});
export type SubscribeCreateOutput = z.infer<
  typeof subscribeCreateOutputSchema
>;

export const subscribeConfirmInputSchema = z.object({
  token: z.string().min(16).max(128),
});

export const subscribeConfirmOutputSchema = z.object({
  status: z.enum(["confirmed", "already-confirmed", "invalid-or-expired"]),
});
export type SubscribeConfirmOutput = z.infer<
  typeof subscribeConfirmOutputSchema
>;

export const subscribeUnsubscribeInputSchema = z.object({
  token: z.string().min(16).max(128),
});

export const subscribeUnsubscribeOutputSchema = z.object({
  status: z.enum(["unsubscribed", "invalid-or-expired"]),
});
export type SubscribeUnsubscribeOutput = z.infer<
  typeof subscribeUnsubscribeOutputSchema
>;

export const subscribePreferencesInputSchema = z.object({
  token: z.string().min(16).max(128),
  digestFrequency: subscriberDigestFrequencySchema.optional(),
  locale: subscriberLocaleSchema.optional(),
  pushEnabled: z.boolean().optional(),
});

export const subscriberSettingsSchema = z.object({
  email: z.string(),
  locale: subscriberLocaleSchema,
  digestFrequency: subscriberDigestFrequencySchema,
  pushEnabled: z.boolean(),
  confirmedAt: z.date().nullable(),
  unsubscribedAt: z.date().nullable(),
});
export type SubscriberSettings = z.infer<typeof subscriberSettingsSchema>;

export const subscribePreferencesOutputSchema = z.object({
  status: z.enum(["updated", "invalid-or-expired"]),
  subscriber: subscriberSettingsSchema.nullable(),
});
export type SubscribePreferencesOutput = z.infer<
  typeof subscribePreferencesOutputSchema
>;

// ----- Web Push opt-in schemas ---------------------------------------------

/**
 * `subscribe.enablePush` — register a PushSubscription against an existing
 * confirmed subscriber. The caller authenticates via the permanent
 * `unsub_token` (same magic-link convention as `subscribe.preferences`).
 */
export const subscribeEnablePushInputSchema = z.object({
  token: z.string().min(16).max(128),
  endpoint: z.string().url().max(2048),
  /** base64url X9.62 SubjectPublicKeyInfo from PushSubscription.toJSON() */
  p256dh: z.string().min(8).max(256),
  /** base64url 16-byte auth secret from PushSubscription.toJSON() */
  auth: z.string().min(8).max(64),
  userAgent: z.string().max(512).optional(),
});
export type SubscribeEnablePushInput = z.infer<
  typeof subscribeEnablePushInputSchema
>;

export const subscribeEnablePushOutputSchema = z.object({
  status: z.enum(["enabled", "invalid-or-expired"]),
});
export type SubscribeEnablePushOutput = z.infer<
  typeof subscribeEnablePushOutputSchema
>;

export const subscribeDisablePushInputSchema = z.object({
  token: z.string().min(16).max(128),
  endpoint: z.string().url().max(2048),
});
export type SubscribeDisablePushInput = z.infer<
  typeof subscribeDisablePushInputSchema
>;

export const subscribeDisablePushOutputSchema = z.object({
  status: z.enum(["disabled", "invalid-or-expired"]),
});
export type SubscribeDisablePushOutput = z.infer<
  typeof subscribeDisablePushOutputSchema
>;

/** DB enum is snake_case; wire format uses hyphen. */
export function digestFrequencyToDb(
  v: SubscriberDigestFrequency,
): "daily" | "weekly" | "urgent_only" {
  return v === "urgent-only" ? "urgent_only" : v;
}

/** Inverse of `digestFrequencyToDb`. */
export function digestFrequencyFromDb(
  v: "daily" | "weekly" | "urgent_only" | "none",
): SubscriberDigestFrequency {
  if (v === "urgent_only") return "urgent-only";
  if (v === "none") return "urgent-only";
  return v;
}
